import { useTranslation } from 'react-i18next';
import { legacyThousands } from '@/entities/legacy-order';
import {
  formatDimensions,
  formatWeight,
  type LegacyRoutePoint,
} from '../lib/order-detail';

/**
 * The cargo manifest — one block per point that carries items, reproducing the
 * old panel's `OrderProducts`.
 *
 * The index badge is a RUNNING counter across pickups and drop-offs together
 * (origins first), not a per-side one, so a two-pickup order numbers its
 * drop-off 3. Pickups are branded orange, drop-offs green.
 *
 * The column grid is the source's 6/1.5/1.5/1.5/1.5 of 12 — expressed here as
 * 4/1/1/1/1 of 8, the same proportions in whole tracks. The first column's
 * header is `single.name`, whose value is "Адрес" — the source's own quirk,
 * kept.
 */
export function LegacyOrderProducts({ points }: { points: LegacyRoutePoint[] }) {
  const { t } = useTranslation('legacy');

  if (points.length === 0) return null;

  return (
    <div className="flex flex-col gap-0 rounded-[24px] border border-[var(--lg-grey-200)] bg-[var(--lg-surface)] px-5 py-4">
      <span className="text-[20px] leading-6 font-medium text-[var(--lg-fg)]">
        {t('single.products')}
      </span>

      {/* The divider sits directly under the title, as the source's
          `borderTop` content box did (no gap between them). */}
      <div className="mt-1 flex w-full flex-col gap-6 border-t border-[var(--lg-grey-200)] pt-4 pb-2">
        {points.map((point, index) => (
          <div key={point.id ?? index} className="flex flex-col gap-3">
            <div className="flex w-full items-center">
              {/* The source's `borderRadius={8}` (32px on a 24px box) clamps
                  to a circle. */}
              <span
                className="flex size-6 shrink-0 items-center justify-center rounded-full text-[12px] font-normal text-[var(--lg-surface)]"
                style={{
                  backgroundColor:
                    point.type === 'origin'
                      ? 'var(--lg-orange-500)'
                      : 'var(--lg-green-500)',
                }}
              >
                {index + 1}
              </span>
              <span className="ml-2 min-w-0 truncate font-normal text-[var(--lg-fg)]">
                {point.address}
              </span>
            </div>

            {/* Column headers — hidden on mobile, where each row stacks. */}
            <div className="hidden grid-cols-8 gap-0 text-[14px] font-normal text-[var(--lg-grey-500)] sm:grid">
              <span className="col-span-4">{t('single.name')}</span>
              <span>{t('single.weight')}</span>
              <span>{t('single.size')}</span>
              <span>{t('single.count')}</span>
              <span>{t('single.price')}</span>
            </div>

            <div className="flex flex-col gap-1">
              {(point.products?.items ?? []).map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className="grid grid-cols-1 gap-1 rounded-xl bg-[var(--lg-bg)] px-4 py-3 text-[16px] font-[450] sm:grid-cols-8 sm:gap-0 sm:px-4"
                >
                  <span className="sm:col-span-4">{item.name || '-'}</span>
                  <span className="max-w-[87px] truncate">
                    {formatWeight(item.weight)}
                  </span>
                  <span>{formatDimensions(item)}</span>
                  <span>{item.quantity}</span>
                  <span>
                    {/* `2x15,000uzs` — no space before the unit, as the source
                        concatenates it directly onto the grouped number. */}
                    {`${item.quantity}x${legacyThousands(item.price_per_unit)}${t('uzs')}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
